import AdminPage from '@cohort/merchants/components/AdminPage';
import Button from '@cohort/merchants/components/buttons/Button';
import {DigitalAssetVisual} from '@cohort/merchants/components/DigitalAssetVisual';
import {PerkAppBadge} from '@cohort/merchants/components/perks/PerkAppBadge';
import PerkRaffleBadge from '@cohort/merchants/components/perks/PerkRaffleBadge';
import {PerkTypeBadge} from '@cohort/merchants/components/perks/PerkTypeBadge';
import {PerkVisual} from '@cohort/merchants/components/PerkVisual';
import {useCurrentCampaign} from '@cohort/merchants/hooks/contexts/currentCampaign';
import {
  getCampaignRewardChangeRoute,
  getDigitalAssetPerksRoute,
  getDigitalAssetSettingsRoute,
} from '@cohort/merchants/lib/Pages';
import {formatDate} from '@cohort/shared/utils/format';
import {
  ArrowsLeftRight,
  ArrowSquareOut,
  Gift,
  Link as LinkIcon,
  PencilSimple,
} from '@phosphor-icons/react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

const CampaignRewardPage = (): JSX.Element => {
  const campaign = useCurrentCampaign();
  const {t} = useTranslation('pages', {keyPrefix: 'campaigns.campaign.reward.page'});

  return (
    <AdminPage header={{title: t('title'), subtitle: t('subtitle')}}>
      <section className="rounded-lg bg-gray-50 p-4">
        <div className="grid gap-8 [grid-template-columns:250px_1fr]">
          <div className="flex flex-col items-start gap-4">
            <DigitalAssetVisual
              digitalAssetImageFileKey={campaign.digitalAssetCollection?.imageFileKey ?? null}
              animationFileKey={campaign.digitalAssetCollection?.animationFileKey ?? null}
              className="rounded-md"
              size={80}
            />
            <div className="text-lg font-bold text-gray-900">
              {campaign.digitalAssetCollection?.internalName}
            </div>
            <Link to={getCampaignRewardChangeRoute(campaign.id).path}>
              <Button>
                <ArrowsLeftRight className="mr-2 h-4 w-4" />
                {t('btnChangeDigitalAsset')}
              </Button>
            </Link>
            <Link to={getDigitalAssetSettingsRoute(campaign.digitalAssetCollection?.id ?? '').path}>
              <Button variant="secondary">
                <ArrowSquareOut className="mr-2 h-4 w-4" />
                {t('btnSeeDigitalAsset')}
              </Button>
            </Link>
          </div>
          {campaign.digitalAssetCollection?.perks &&
          campaign.digitalAssetCollection.perks.length > 0 ? (
            <ul className="flex flex-col gap-4 border-l pl-8">
              {campaign.digitalAssetCollection.perks.map(perk => (
                <li
                  key={perk.id}
                  className="grid gap-4 [grid-template-columns:max-content_1fr_max-content]"
                >
                  <PerkVisual perk={perk} />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{perk.internalName}</h3>
                    <div className="mt-1 flex items-center gap-2">
                      <PerkTypeBadge perkType={perk.type} />
                      <PerkAppBadge
                        perkIntegrationId={perk.integration?.perkIntegrationId ?? null}
                      />
                      {perk.raffle !== null && <PerkRaffleBadge />}
                    </div>
                  </div>
                  <div className="self-center text-xs text-gray-500">
                    {t('labelLastEdit')} {formatDate(perk.updatedAt)}
                  </div>
                </li>
              ))}
              <li className="self-end">
                <Link to={getDigitalAssetPerksRoute(campaign.digitalAssetCollection.id).path}>
                  <Button variant="secondary">
                    <PencilSimple className="mr-2 h-4 w-4" />
                    {t('btnEditPerks')}
                  </Button>
                </Link>
              </li>
            </ul>
          ) : (
            <div className="flex flex-col items-center justify-center border-l pl-8">
              <Gift className="h-8 w-8 text-primary" />
              <span className="mt-2 text-sm font-medium text-gray-900">{t('noPerks')}</span>
              <Link
                to={`${
                  getDigitalAssetPerksRoute(campaign.digitalAssetCollection?.id || '').path
                }?add=true`}
              >
                <Button className="mt-4">
                  <LinkIcon className="mr-2 h-4 w-4" />
                  {t('btnLinkPerks')}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>
    </AdminPage>
  );
};

export default CampaignRewardPage;
